import { format, isSameDay, isValid, parseISO } from 'date-fns'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  ApprovalStatus,
  kuiperClassNames,
  navigateToContent,
} from '@opallabs/kuiper'
import { Icon, Tooltip } from '@opallabs/lapidary'
import { AllowedEvents, analyticsContext } from '@opallabs/quanta'
import { ChannelIcon } from '@opallabs/satellite'
import { selectorHelpers } from '@opallabs/wormhole'
import CardContext from '../cardContext'
import { useContentDragContext } from '../ContentDragContext'
import { setContentDragData } from '../MomentCard/dragData'
import RenamePopover from '../MomentCard/RenamePopover'
import { TooltippedStoryHeading } from '../MomentCard/StoryHeading'
import * as cardContainerStyles from '../MomentCard/styles'
import { contentHasScheduledTime } from '../momentTypes'
import type { PartialContent, PartialMoment } from '../momentTypes'
import { useWorkspaceServiceIconMap } from '../queries/useSystemServices'
import { ContentNamePriority } from '../types'
import { WeekViewGlobalContext } from '../weekViewGlobalContext'
import { useWeekViewSettings } from '../WeekViewSettingsContext'
import { ContentCardContextMenu } from './ContentCardContextMenu'
import { useCardStripeColor } from './useCardStripeColor'

import './styles.scss'

// When only a start is scheduled we just render the time; when a takedown is
// also set we render it as a range, promoting to `MMM d, h:mm a` on the end
// side if the takedown falls on a different calendar day from the start.
const formatScheduleRange = (
  startIso: string,
  endIso: string | null | undefined
): string => {
  const start = parseISO(startIso)
  const startLabel = format(start, 'h:mm a')
  if (!endIso) return startLabel

  const end = parseISO(endIso)
  const endLabel = isSameDay(start, end)
    ? format(end, 'h:mm a')
    : format(end, 'MMM d, h:mm a')
  return `${startLabel} – ${endLabel}`
}

// Clock-only — span bars encode date via their column position.
const formatSpanTimeRange = (
  startIso: string,
  endIso: string | null | undefined
): string => {
  const startLabel = format(parseISO(startIso), 'h:mm a')
  if (!endIso) return startLabel
  return `${startLabel} – ${format(parseISO(endIso), 'h:mm a')}`
}

type ContentCardProps = {
  content: PartialContent
  moment: PartialMoment | undefined
  position?: number
  // Condensed bar variant for span mode. Clamped flags drop the
  // border-radius on edges whose in-market window extends past the week.
  isSpanning?: boolean
  clampedLeft?: boolean
  clampedRight?: boolean
  // Gate drag on parent-moment hydration: without a resolved moment, the
  // drop-target guards (which compare against moment bounds) can't enforce
  // the window and users could drop the post outside its parent moment.
  isDraggable?: boolean
}

/**
 * Content-primary card rendered in Content mode. Each content item gets its
 * own top-level card positioned at its own `scheduled_at` (not the parent
 * moment's). Carries a label-set stripe (keyed to whichever set is driving
 * the current sort, falling back to the brand's primary label set) plus a
 * story color as two distinct color signals.
 *
 * Sibling: `MomentCard/Default/index.tsx` renders the moment-primary card in
 * Moment mode. ContentCard and DefaultMomentCard don't share a layout, but
 * they share: `CardContext` callbacks; `<AnalyticsLayer>` wrappers via
 * `CategoryRows/ContentStack` / `CategoryRows/MomentStack`; and — starting
 * here — the drag shell (`kuiper-draggableCardContainer` +
 * `kuiper-cardDragShadow`) plus the shared Emotion container styles from
 * `MomentCard/styles.ts`. Design can diverge later by theming those
 * primitives; until then, both modes look and feel the same.
 */
export const ContentCard: React.VFC<ContentCardProps> = ({
  content,
  moment,
  position = 0,
  isSpanning = false,
  clampedLeft = false,
  clampedRight = false,
  isDraggable = false,
}) => {
  const { currentBrandId } = useContext(WeekViewGlobalContext)
  const {
    contextMenuCallbacks,
    navigateTo,
    setDragOrigin,
    setIsDraggingContent,
  } = useContext(CardContext) ?? {}
  const { setDraggedContent, setHoveredDate } = useContentDragContext()
  const { collapseContent, namePriority } = useWeekViewSettings()
  const { data: serviceIconMap } = useWorkspaceServiceIconMap(currentBrandId)
  const { track } = useContext(analyticsContext)

  const [isRenamePopoverOpen, setIsRenamePopoverOpen] = useState(false)
  // Local `contentName` for optimistic display while the rename PATCH is
  // in flight. Kept in sync with `content.name` so upstream updates (e.g.
  // Redux refetch) reflect here. Mirrors `DefaultContentListItem`.
  const [contentName, setContentName] = useState(content.name ?? '')
  useEffect(() => {
    setContentName(content.name ?? '')
  }, [content.name])

  // Drag shell state — mirrors `MomentCard/withDraggable.js`. During drag
  // the card body is swapped for a `kuiper-cardDragShadow` placeholder at
  // the same height, so the grid layout stays stable.
  const [isDragging, setIsDragging] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const dragShadowHeight =
    isDragging && cardRef.current
      ? cardRef.current.getBoundingClientRect().height
      : 0

  // Expanded mode renders the content snapshot above the body; collapsed
  // mode hides it. Toggled by the "Content Image" setting in SettingsMenu.
  const isExpanded = !collapseContent

  // Show the content's own primary asset (its first by position), resolved
  // through the same cover/file-fallback chain moment cards use so videos and
  // non-previewable files still get an image — a sized proxy image that's cheap
  // to render. Falls back to the parent moment's primary asset when the post
  // has none.
  const previewUrl = useMemo(() => {
    const primaryAsset = content.asset_references?.[0]
    if (primaryAsset) {
      const image = selectorHelpers.getRelevantImageProperties({
        primary_asset_reference: primaryAsset,
        asset_references: content.asset_references,
      })
      if (image?.url) return image.url
    }
    const fromMoment = moment?.primary_asset_reference
    return fromMoment?.preview_url ?? fromMoment?.full_url ?? null
  }, [content, moment])

  const serviceIcon = useMemo(
    () =>
      content.service?.id ? serviceIconMap?.[content.service.id] : undefined,
    [content.service?.id, serviceIconMap]
  )

  const stripeColor = useCardStripeColor(content, moment, serviceIcon?.color)

  // The "Primary Name" setting promotes the moment title into the bold title
  // slot and drops the content name to the muted secondary row. Only applies
  // when there's a moment title to promote, so contentless-moment cards and
  // moments without a title keep the content name primary.
  const momentIsPrimary =
    namePriority === ContentNamePriority.Moment && Boolean(moment?.title)
  // `momentIsPrimary` guarantees a moment title, so the `?? ''` is only to
  // satisfy the type — it's never the rendered value.
  const primaryName = momentIsPrimary
    ? (moment?.title ?? '')
    : contentName || 'Untitled content'
  const secondaryName = contentName || 'Untitled content'

  // No label when no time was chosen — `scheduled_at` would render the
  // moment's midnight fallback as if it were a real 12:00 AM.
  const timeLabel =
    content.scheduled_at && contentHasScheduledTime(content)
      ? (isSpanning ? formatSpanTimeRange : formatScheduleRange)(
          content.scheduled_at,
          content.takedown_scheduled_at
        )
      : null

  // Story chip mirrors moment-card behavior; render only when the parent
  // moment carries a primary story.
  const primaryStory = moment?.primaryStory

  // Span bars render in every week their range touches but the moment is only
  // hydrated in overlap weeks. Fall back to the relationship id so the bar
  // stays clickable — and labels as a generic moment link — from any week.
  const resolvedMomentId = moment?.id ?? content.relationships?.moment?.id

  const onClick = () => {
    if (!resolvedMomentId) return
    // Suppress navigation while the rename popover is visible so clicks
    // inside the popover don't trigger a navigate away. Mirrors
    // `DefaultContentListItem.onClickContent`.
    if (isRenamePopoverOpen) return
    track(AllowedEvents.click, { id: content.id, type: 'content' })
    navigateToContent({
      contentId: content.id,
      momentId: resolvedMomentId,
      navigateTo,
      primaryBrand: moment?.primaryBrand,
    })
  }

  const onRenameContent = (id: string, newName: string) => {
    setContentName(newName)
    contextMenuCallbacks?.renameContent?.(id, newName)
  }

  const onDragStart = (e: React.DragEvent) => {
    e.stopPropagation()

    const dragData = {
      contentId: content.id,
      momentId: moment?.id ?? '',
      contentPosition: position,
    }
    // dataTransfer must be set synchronously inside dragstart — the browser
    // requires it before the event returns. The card itself is used as the
    // drag ghost so the cursor preview matches exactly what the user is
    // dragging (a single post, not a moment); that's the same approach
    // browsers take by default for non-customized drag sources.
    setContentDragData(e, dragData)
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move'
      if (cardRef.current) {
        e.dataTransfer.setDragImage(cardRef.current, 62, 10)
      }
    }

    track(AllowedEvents.drag, { id: content.id, type: 'content' })

    // setTimeout addresses a Chrome bug that fires dragend immediately after
    // dragstart when state updates synchronously mutate the drag source's
    // React tree. Mirrors the pattern in `MomentCard/withDraggable.js`.
    setTimeout(() => {
      setDragOrigin?.(dragData)
      setIsDraggingContent?.(true)
      setIsDragging(true)
      setDraggedContent({
        id: content.id,
        momentId: moment?.id ?? '',
        momentScheduledAt: moment?.scheduled_at ?? null,
        momentScheduledEndAt: moment?.scheduled_end_at ?? null,
        scheduledAt: content.scheduled_at ?? null,
        takedownAt: content.takedown_scheduled_at ?? null,
        contentPosition: position,
      })
    }, 0)
  }

  const onDragEnd = () => {
    setIsDraggingContent?.(false)
    setIsDragging(false)
    setDraggedContent(null)
    // Clear the column-hover signal so `WeekViewDayHeader` stops rendering
    // the content drop overlay as soon as the drag ends (even if no drop
    // landed). Without this the overlay would linger for a frame.
    setHoveredDate(null)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    onClick()
  }

  // Matches the status dot on moment mode's content tokens. The statuses are
  // passed as an array rather than a `content` object because `ApprovalStatus`
  // wants a full `PartialPost`; note the array is resolved down to a single
  // status, since `generateStatusString` concatenates entries into a class
  // string that the icon lookup then can't match.
  const workflowStatus = (
    <span className={kuiperClassNames('contentCard-status')}>
      <ApprovalStatus
        approvalStatuses={
          content.is_published ? ['published'] : [content.status ?? 'draft']
        }
        hideDeliveryRing
      />
    </span>
  )

  // Card body — inner chrome (label stripe, preview, body rows). Click lives
  // here, not on the outer wrapper: the context menu renders its items in a
  // Radix portal that bubbles clicks up the React tree, so a wrapper-level
  // onClick would fire (and navigate) when "Rename" is chosen. Keyboard focus
  // / Enter activation lives on the outer wrapper below (matching
  // `MomentCard/withDraggable.js`), which is where the gem `FocusIndicator`
  // boxShadow expects to see `:focus`.
  const cardBodyElement = (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={kuiperClassNames('contentCard', {
        'contentCard-isExpanded': isExpanded,
        'contentCard-isSpanning': isSpanning,
        'contentCard-hasStripe': Boolean(stripeColor),
        'contentCard-clampedLeft': clampedLeft,
        'contentCard-clampedRight': clampedRight,
      })}
      onClick={onClick}
    >
      {stripeColor && (
        <div
          className={kuiperClassNames('contentCard-labelStripe')}
          style={{ backgroundColor: stripeColor }}
          aria-hidden
        />
      )}
      {!isSpanning && isExpanded && previewUrl && (
        <img
          className={kuiperClassNames('contentCard-preview')}
          src={previewUrl}
          alt=""
          aria-hidden
        />
      )}
      {isSpanning ? (
        <div className={kuiperClassNames('contentCard-condensedBody')}>
          <div className={kuiperClassNames('contentCard-condensedRow')}>
            <Tooltip
              content={
                <span className={kuiperClassNames('contentCard-tooltipLabel')}>
                  {primaryName}
                </span>
              }
              side="top"
              zIndex={999}
            >
              <span className={kuiperClassNames('contentCard-condensedTitle')}>
                {primaryName}
              </span>
            </Tooltip>
            {timeLabel && (
              <span className={kuiperClassNames('contentCard-condensedTime')}>
                {timeLabel}
              </span>
            )}
          </div>
          <div className={kuiperClassNames('contentCard-condensedRow')}>
            <span className={kuiperClassNames('contentCard-condensedAccount')}>
              {serviceIcon && (
                <span
                  className={kuiperClassNames(
                    'contentCard-condensedChannelIcon'
                  )}
                >
                  <ChannelIcon
                    iconSize={12}
                    imageSrc={serviceIcon.assetSrc}
                    channelColor={serviceIcon.color}
                    channelName={serviceIcon.name}
                    channelIconName={serviceIcon.iconName ?? undefined}
                    channelSystemName={serviceIcon.systemName}
                    privateForUser={false}
                  />
                </span>
              )}
              <span
                className={kuiperClassNames(
                  'contentCard-condensedAccountName',
                  {
                    'contentCard-condensedAccountName-empty':
                      !content.account?.name,
                  }
                )}
              >
                {content.account?.name || 'No account'}
              </span>
            </span>
            {momentIsPrimary ? (
              <span className={kuiperClassNames('contentCard-condensedMoment')}>
                <span>{secondaryName}</span>
              </span>
            ) : (
              (moment?.title || resolvedMomentId) && (
                <span
                  className={kuiperClassNames('contentCard-condensedMoment')}
                >
                  <Icon
                    ariaHidden
                    glyph="gem-moment-outlined"
                    height={12}
                    width={12}
                  />
                  <span>{moment?.title ?? 'View moment'}</span>
                </span>
              )
            )}
            {workflowStatus}
          </div>
        </div>
      ) : (
        <>
          <div className={kuiperClassNames('contentCard-body')}>
            <div className={kuiperClassNames('contentCard-attributes')}>
              {primaryStory && moment && (
                <TooltippedStoryHeading moment={moment} />
              )}
              {momentIsPrimary ? (
                <Tooltip
                  content={
                    <span
                      className={kuiperClassNames('contentCard-tooltipLabel')}
                    >
                      {secondaryName}
                    </span>
                  }
                  side="top"
                  zIndex={999}
                >
                  <div className={kuiperClassNames('contentCard-momentName')}>
                    <span>{secondaryName}</span>
                  </div>
                </Tooltip>
              ) : (
                moment?.title && (
                  <div className={kuiperClassNames('contentCard-momentName')}>
                    <span
                      className={kuiperClassNames('contentCard-momentIcon')}
                    >
                      <Icon
                        ariaHidden
                        glyph="gem-moment-outlined"
                        height={14}
                        width={14}
                      />
                    </span>
                    <span>{moment.title}</span>
                  </div>
                )
              )}
            </div>
            <Tooltip
              content={
                <span className={kuiperClassNames('contentCard-tooltipLabel')}>
                  {primaryName}
                </span>
              }
              side="top"
              zIndex={999}
            >
              <div className={kuiperClassNames('contentCard-title')}>
                {primaryName}
              </div>
            </Tooltip>
            {timeLabel && (
              <div className={kuiperClassNames('contentCard-meta')}>
                {timeLabel}
              </div>
            )}
          </div>
          <div className={kuiperClassNames('contentCard-footer')}>
            <span className={kuiperClassNames('contentCard-footerContext')}>
              {serviceIcon && (
                <span className={kuiperClassNames('contentCard-channelIcon')}>
                  <ChannelIcon
                    iconSize={14}
                    imageSrc={serviceIcon.assetSrc}
                    channelColor={serviceIcon.color}
                    channelName={serviceIcon.name}
                    channelIconName={serviceIcon.iconName ?? undefined}
                    channelSystemName={serviceIcon.systemName}
                    privateForUser={false}
                  />
                </span>
              )}
              {content.account?.name && (
                <span className={kuiperClassNames('contentCard-accountName')}>
                  {content.account.name}
                </span>
              )}
            </span>
            {workflowStatus}
          </div>
        </>
      )}
    </div>
  )

  // Wrap in context menu + rename popover when we have the parent moment
  // (duplicate/delete callbacks require it). Without it, just render the
  // bare card — still draggable, still clickable, just no menu.
  const wrappedBody =
    !moment || !contextMenuCallbacks ? (
      cardBodyElement
    ) : (
      <ContentCardContextMenu
        content={content}
        moment={moment}
        contextMenuCallbacks={contextMenuCallbacks}
        setIsRenamePopoverOpen={setIsRenamePopoverOpen}
        track={track}
      >
        {isRenamePopoverOpen ? (
          <RenamePopover
            closePopover={() => setIsRenamePopoverOpen(false)}
            entity={content}
            isOpen={isRenamePopoverOpen}
            label="Rename Content"
            maxLength={100}
            renameEntity={onRenameContent}
            trigger={cardBodyElement}
          />
        ) : (
          cardBodyElement
        )}
      </ContentCardContextMenu>
    )

  const scheduledAtDate = content.scheduled_at
    ? parseISO(content.scheduled_at)
    : null
  const ariaLabel =
    scheduledAtDate && isValid(scheduledAtDate)
      ? `${content.name ?? 'Content'} ${format(scheduledAtDate, 'EEEE LLLL do yyyy')}`
      : (content.name ?? 'Content')

  // Outer drag shell — shares `kuiper-draggableCardContainer` +
  // `cardContainerStyles.container` with `MomentCard/withDraggable.js` so
  // shadow, hover, and focus behaviors stay identical across modes.
  // `tabIndex` + `role` sit on the outer (not the inner body) because
  // `cardContainerStyles.container`'s `:focus` pseudo expects to match here.
  return (
    <div
      aria-label={ariaLabel}
      className={kuiperClassNames('draggableCardContainer', { isDragging })}
      css={cardContainerStyles.container({ theme: 'light' })}
      draggable={isDraggable}
      onDragEnd={onDragEnd}
      onDragStart={onDragStart}
      onKeyDown={onKeyDown}
      ref={cardRef}
      role="button"
      tabIndex={0}
    >
      {isDragging ? (
        <div
          className={kuiperClassNames('cardDragShadow', { isDragging })}
          style={{ blockSize: dragShadowHeight }}
        />
      ) : (
        wrappedBody
      )}
    </div>
  )
}
